import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type OptionTone = 'blue' | 'primary';

const toneStyles: Record<OptionTone, { ring: string; selected: string }> = {
  blue: {
    ring: 'focus-visible:ring-blue-500',
    selected: 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100',
  },
  primary: {
    ring: 'focus-visible:ring-primary-500',
    selected: 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-900 dark:text-primary-100',
  },
};

const idleStyle =
  'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800';

export const submissionFieldLabelClass = 'block text-sm font-medium text-slate-800 dark:text-slate-200';
export const submissionInputClass =
  'w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
export const submissionHelperTextClass = 'text-xs text-slate-600 dark:text-slate-300';

export function getSelectableOptionClass({
  selected,
  tone = 'blue',
  compact = false,
}: {
  selected: boolean;
  tone?: OptionTone;
  compact?: boolean;
}): string {
  const toneStyle = toneStyles[tone];
  const spacing = compact ? 'p-2 text-sm' : 'px-3 py-2 text-sm';
  const variant = selected ? toneStyle.selected : idleStyle;
  return `w-full text-left rounded-md border transition-colors focus:outline-none focus-visible:ring-2 ${toneStyle.ring} ${spacing} ${variant}`;
}

interface SelectableOptionButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  children: ReactNode;
  selected: boolean;
  tone?: OptionTone;
  compact?: boolean;
  className?: string;
}

export function SelectableOptionButton({
  children,
  selected,
  tone = 'blue',
  compact = false,
  className = '',
  type = 'button',
  ...props
}: SelectableOptionButtonProps) {
  const classes = `${getSelectableOptionClass({ selected, tone, compact })} ${className}`.trim();
  return (
    <button type={type} className={classes} {...props}>
      {children}
    </button>
  );
}

